import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);
  #bundleRemoveLoaderStartedAt = 0;

  #getCartRowByLine(line) {
    const numericLine = Number(line);
    if (!Number.isFinite(numericLine) || numericLine <= 0) return null;
    const rowSelector = `[ref="cartItemRows[]"][data-cart-line="${numericLine}"]`;
    const row = this.querySelector(rowSelector);
    return row instanceof HTMLTableRowElement ? row : null;
  }

  #showBundleRemoveLoader() {
    this.#bundleRemoveLoaderStartedAt = Date.now();
    this.classList.add('cart-items-removing-bundle');
  }

  #hideBundleRemoveLoader() {
    const elapsed = Date.now() - this.#bundleRemoveLoaderStartedAt;
    const delay = Math.max(0, 700 - elapsed);
    window.setTimeout(() => {
      this.classList.remove('cart-items-removing-bundle');
    }, delay);
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.#getCartRowByLine(line);

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    const cartItemRowToRemove = this.#getCartRowByLine(line);
    const bundleToken = cartItemRowToRemove?.dataset?.ppnBundleToken || '';
    const removedLineKey = cartItemRowToRemove?.dataset?.key || '';
    const linkedBundleTokenPromise = this.#resolveBundleTokenForLine(line, bundleToken);
    console.log('[CartItems] Remove requested', { line, bundleToken, removedLineKey });

    this.#showBundleRemoveLoader();
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    if (!cartItemRowToRemove) {
      console.warn('[CartItems] Remove row not found for line', { line });
      this.#hideBundleRemoveLoader();
      return;
    }

    linkedBundleTokenPromise
      .then((resolvedToken) => {
        console.log('[CartItems] Bundle token resolved', { line, resolvedToken, removedLineKey });
        if (!resolvedToken) return;
        return this.#removeLinkedPlaysetItems(resolvedToken, removedLineKey);
      })
      .catch((error) => {
        console.error('[CartItems] Linked playset remove failed before update.', error);
      })
      .finally(() => {
        this.#hideBundleRemoveLoader();
      });

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // If the cart item row is the last row, optimistically trigger the cart empty state
    const isEmptyCart = rowsToRemove.length == this.refs.cartItemRows.length;

    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);

      startViewTransition(() => {
        this.replaceChildren(clone);
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

      return;
    }

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  #resolveBundleTokenForLine(line, bundleToken) {
    if (bundleToken) return Promise.resolve(bundleToken);

    const cartItemRow = this.#getCartRowByLine(line);
    const rowBundleToken = cartItemRow?.dataset?.ppnBundleToken || '';
    if (rowBundleToken) {
      console.log('[CartItems] Resolve token from row dataset', {
        line,
        lineItemKey: cartItemRow?.dataset?.key || '',
        rowBundleToken,
      });
      return Promise.resolve(rowBundleToken);
    }

    return fetch(Theme.routes.cart_url + '.js', fetchConfig())
      .then((response) => response.json())
      .then((cart) => {
        if (!cart || !Array.isArray(cart.items)) return '';
        const lineItem = cart.items[line - 1];
        const properties = lineItem && lineItem.properties ? lineItem.properties : null;
        console.log('[CartItems] Resolve token from cart line', {
          line,
          lineItemKey: lineItem && lineItem.key ? lineItem.key : '',
          lineItemProperties: properties || {},
        });
        return properties && typeof properties._ppn_bundle_token === 'string' ? properties._ppn_bundle_token : '';
      })
      .catch(() => '');
  }

  #removeLinkedPlaysetItems(bundleToken, removedLineKey) {
    console.log('[CartItems] Remove linked playsets start', { bundleToken, removedLineKey });
    return fetch(Theme.routes.cart_url + '.js', fetchConfig())
      .then((response) => response.json())
      .then((cart) => {
        if (!cart || !Array.isArray(cart.items)) return;

        const updates = {};
        cart.items.forEach((item) => {
          const properties = item && item.properties ? item.properties : null;
          if (!properties) return;
          if (properties._ppn_hidden_playset !== 'true') return;
          if (properties._ppn_bundle_token !== bundleToken) return;
          if (!item.key || item.key === removedLineKey) return;
          updates[item.key] = 0;
        });

        console.log('[CartItems] Linked playset candidates', {
          bundleToken,
          removedLineKey,
          updates,
          items: cart.items.map((item) => ({
            key: item.key,
            title: item.product_title,
            quantity: item.quantity,
            properties: item.properties || {},
          })),
        });

        if (Object.keys(updates).length === 0) return;

        const cartItemsComponents = document.querySelectorAll('cart-items-component');
        const sectionsToUpdate = new Set([this.sectionId]);
        cartItemsComponents.forEach((item) => {
          if (item instanceof HTMLElement && item.dataset.sectionId) {
            sectionsToUpdate.add(item.dataset.sectionId);
          }
        });

        const body = JSON.stringify({
          updates,
          sections: Array.from(sectionsToUpdate).join(','),
          sections_url: window.location.pathname,
        });

        return fetch(Theme.routes.cart_update_url, fetchConfig('json', { body }))
          .then((response) => response.json())
          .then((parsedResponseText) => {
            console.log('[CartItems] Linked playset remove response', {
              itemCount: parsedResponseText && typeof parsedResponseText.item_count === 'number' ? parsedResponseText.item_count : null,
              sectionsUpdated: parsedResponseText && parsedResponseText.sections ? Object.keys(parsedResponseText.sections) : [],
            });
            this.dispatchEvent(
              new CartUpdateEvent(parsedResponseText, this.sectionId, {
                source: 'cart-items-component',
                sections: parsedResponseText.sections,
              })
            );
          });
      })
      .catch((error) => {
        console.error('[CartItems] Linked playset remove failed.', error);
      });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], this.isDrawer ? 'hydration' : 'full', { injectStylesheet: true });

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml, 'full', { injectStylesheet: true });

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
